import { ReactNode, useState } from 'react'
import {
  WppButton,
  WppInlineMessage,
  WppModal,
  WppTextareaInput,
  WppTypography,
} from '@wppopen/components-library-react'
import { ERROR_MESSAGES } from 'utils/constants'
import { type FieldsSettingsProps, type OnSubmitGenericModalProps, handleInputChange } from './helper'
import type { FEApiError } from 'utils/errorHandling'
import modalStyles from '../Modal.module.scss'

interface GenericModalProps {
  onSubmit: ({ comment }: OnSubmitGenericModalProps) => Promise<void>
  onCancel?: () => void
  modalTitle: string
  submitButtonText: string
  isApiLoading: boolean
  shouldModalOpen: boolean
  fieldsConfig?: FieldsSettingsProps
  children?: ReactNode
}

export default function GenericModal({
  onSubmit,
  onCancel,
  modalTitle,
  submitButtonText,
  isApiLoading,
  shouldModalOpen,
  fieldsConfig,
  children,
}: GenericModalProps) {
  const { MANDATORY_FIELD } = ERROR_MESSAGES
  const [comment, setComment] = useState('')
  const [commentError, setCommentError] = useState<string>()
  const [errorMessage, setErrorMessage] = useState<string>()

  const validateFields = (): boolean => {
    const errors = {
      comment: fieldsConfig?.isCommentRequiredField && !comment ? MANDATORY_FIELD : undefined,
    }

    setCommentError(errors.comment)

    return !errors.comment
  }

  const handleInternalSubmit = () => {
    if (!validateFields()) return
    setErrorMessage(undefined)

    onSubmit({
      comment: fieldsConfig?.hasCommentField ? comment : undefined,
    }).catch((err: FEApiError) => {
      const { errors, message } = err

      if (errors) {
        errors.forEach(({ message, path }) => {
          if (path.match('comment')) setCommentError(message)
        })

        return errors
      } else if (message) {
        setErrorMessage(message)
      }
    })
  }

  return (
    <WppModal
      open={shouldModalOpen}
      className={modalStyles.modal}
      formConfig={{
        onSubmit: e => {
          e.preventDefault()
          handleInternalSubmit()
        },
        onReset: () => {
          if (onCancel) onCancel()
        },
      }}
      data-testid="generic-modal"
    >
      <WppTypography tag="h3" type="xl-heading" slot="header">
        {modalTitle}
      </WppTypography>
      <div slot="body">
        {children}

        {fieldsConfig?.hasCommentField && (
          <WppTextareaInput
            required={fieldsConfig.isCommentRequiredField}
            data-testid="comment-input"
            name="reason"
            placeholder="Enter your comment"
            labelConfig={{
              text: 'Comment',
            }}
            rows={3}
            messageType={!!commentError ? 'error' : undefined}
            message={!!commentError ? commentError : undefined}
            value={comment}
            disabled={isApiLoading}
            onWppChange={e => {
              const { value } = e.target
              const { isCommentRequiredField } = fieldsConfig
              handleInputChange(setComment, setCommentError, value, isCommentRequiredField)
            }}
          />
        )}
      </div>

      <div slot="actions">
        <div className={modalStyles.modal__actionsGroup}>
          {onCancel && (
            <WppButton type="reset" variant="secondary" data-testid="modal-reset-button" disabled={isApiLoading}>
              Cancel
            </WppButton>
          )}
          <WppButton type="submit" variant="primary" data-testid="modal-submit-button" loading={isApiLoading}>
            {submitButtonText}
          </WppButton>
        </div>
        {errorMessage && (
          <WppInlineMessage data-testid="modal-error-inline" size="s" message={errorMessage} type="error" />
        )}
      </div>
    </WppModal>
  )
}
